import React, { useState, useMemo } from 'react';
import {
  PatientRecord,
  formatCurrency,
  getStatusColor
} from '@/lib/index';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ChevronLeft,
  ChevronRight,
  Search,
  ArrowUpDown,
  Activity,
  MoreHorizontal,
  User,
  Calendar,
  Filter
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

interface DataTableProps {
  data: PatientRecord[];
  columns: string[];
  onRowClick?: (record: PatientRecord) => void;
}

export function DataTable({ data, columns, onRowClick }: DataTableProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState<{ key: keyof PatientRecord; direction: 'asc' | 'desc' } | null>(null);
  const itemsPerPage = 10;

  const handleSort = (key: string) => {
    const k = key as keyof PatientRecord;
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === k && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key: k, direction });
  };

  const filteredData = useMemo(() => {
    let result = [...data];
    
    if (searchTerm) {
      result = result.filter(patient => 
        patient.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        patient.id.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (sortConfig) {
      result.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [data, searchTerm, sortConfig]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const renderCell = (record: PatientRecord, column: string) => {
    const key = column.toLowerCase() as keyof PatientRecord;
    const value = record[key];

    switch (column.toLowerCase()) {
      case 'status':
        return (
          <Badge 
            className="font-medium"
            style={{ backgroundColor: `${getStatusColor(record.status)}20`, color: getStatusColor(record.status), border: `1px solid ${getStatusColor(record.status)}40` }}
          >
            {record.status}
          </Badge>
        );
      case 'charges':
        return <span className="font-mono font-medium text-primary">{formatCurrency(record.charges)}</span>;
      case 'riskscore':
        return (
          <div className="flex items-center gap-2">
            <div className="w-12 h-1.5 bg-muted rounded-full overflow-hidden">
              <div 
                className="h-full transition-all duration-500"
                style={{ 
                  width: `${record.riskScore}%`, 
                  backgroundColor: record.riskScore > 75 ? 'oklch(0.55 0.18 28)' : record.riskScore > 40 ? 'oklch(0.82 0.14 85)' : 'oklch(0.68 0.16 165)'
                }}
              />
            </div>
            <span className="text-xs font-mono">{record.riskScore}%</span>
          </div>
        );
      case 'name':
        return (
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary">
              <User size={16} />
            </div>
            <span className="font-semibold text-foreground">{record.name}</span>
          </div>
        );
      case 'lastvisit':
        return (
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <Calendar size={14} />
            <span className="text-sm">{record.lastVisit}</span>
          </div>
        );
      default:
        return <span className="capitalize">{String(value)}</span>;
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
        <div className="relative w-full md:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Search patient name or ID..."
            className="pl-10 bg-card border-border/40 focus:ring-primary/20"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="gap-2">
            <Filter size={14} />
            Filters
          </Button>
          <Button variant="outline" size="sm" className="gap-2">
            <Activity size={14} />
            Export PDF
          </Button>
        </div>
      </div>

      <div className="rounded-xl border border-border/40 bg-card/50 backdrop-blur-sm overflow-hidden shadow-sm">
        <Table>
          <TableHeader className="bg-muted/30">
            <TableRow className="hover:bg-transparent border-border/40">
              {columns.map((col) => (
                <TableHead 
                  key={col} 
                  className="cursor-pointer hover:text-primary transition-colors py-4 px-6"
                  onClick={() => handleSort(col.toLowerCase())}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                      {col.replace(/([A-Z])/g, ' $1').trim()}
                    </span>
                    <ArrowUpDown size={12} className="text-muted-foreground/50" />
                  </div>
                </TableHead>
              ))}
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            <AnimatePresence mode="popLayout">
              {paginatedData.map((record, index) => (
                <motion.tr
                  key={record.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.2, delay: index * 0.03 }}
                  className={cn(
                    "group cursor-pointer hover:bg-primary/5 transition-colors border-border/30",
                    onRowClick && "active:scale-[0.99]"
                  )}
                  onClick={() => onRowClick?.(record)}
                >
                  {columns.map((col) => (
                    <TableCell key={`${record.id}-${col}`} className="py-4 px-6">
                      {renderCell(record, col)}
                    </TableCell>
                  ))}
                  <TableCell className="text-right pr-6">
                    <Button variant="ghost" size="icon" className="opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8">
                      <MoreHorizontal size={16} />
                    </Button>
                  </TableCell>
                </motion.tr>
              ))}
            </AnimatePresence>
            {paginatedData.length === 0 && (
              <TableRow>
                <TableCell colSpan={columns.length + 1} className="h-32 text-center text-muted-foreground">
                  No records found matching your criteria.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between px-2">
        <div className="text-sm text-muted-foreground">
          Showing <span className="font-medium text-foreground">{(currentPage - 1) * itemsPerPage + 1}</span> to <span className="font-medium text-foreground">{Math.min(currentPage * itemsPerPage, filteredData.length)}</span> of <span className="font-medium text-foreground">{filteredData.length}</span> records
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8 border-border/40"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(p => p - 1)}
          >
            <ChevronLeft size={16} />
          </Button>
          <div className="flex items-center gap-1">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum = i + 1;
              if (totalPages > 5 && currentPage > 3) {
                pageNum = currentPage - 3 + i + 1;
                if (pageNum > totalPages) pageNum = totalPages - (4 - i);
              }
              return (
                <Button
                  key={pageNum}
                  variant={currentPage === pageNum ? "default" : "ghost"}
                  size="sm"
                  className={cn(
                    "h-8 w-8",
                    currentPage === pageNum ? "bg-primary shadow-sm" : "text-muted-foreground"
                  )}
                  onClick={() => setCurrentPage(pageNum)}
                >
                  {pageNum}
                </Button>
              );
            })}
          </div>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8 border-border/40"
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(p => p + 1)}
          >
            <ChevronRight size={16} />
          </Button>
        </div>
      </div>
    </div>
  );
}

export function PatientTable({ data, onRowClick }: { data: PatientRecord[], onRowClick?: (record: PatientRecord) => void }) {
  const columns = ['Name', 'Age', 'Status', 'TreatmentType', 'RiskScore', 'Charges', 'LastVisit'];
  
  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Patient Records</h2>
          <p className="text-muted-foreground text-sm">Manage and monitor patient health metrics and financial data.</p>
        </div>
      </div>
      <DataTable 
        data={data} 
        columns={columns} 
        onRowClick={onRowClick} 
      />
    </div>
  );
}